/**
 * Contrat de l'API des ventes — Shopify.
 *
 * Même règle que `stats.ts` : importé par le backend ET par le frontend via
 * l'alias `@shared/*`, jamais redéclaré d'un côté ou de l'autre.
 *
 * ── D'où viennent les chiffres ──────────────────────────────────────────────
 * Des tables `shopify_*`, tenues à jour par le worker `sync_shopify` toutes les
 * dix minutes environ (backend/src/sync/shopify/). À la différence du reste du
 * dashboard, ces données ne dépendent PAS du sync nocturne de prod.
 *
 * ── Périmètre ───────────────────────────────────────────────────────────────
 * Commandes de test et commandes annulées exclues. Une annulation Shopify
 * s'accompagne presque toujours d'un remboursement intégral : garder la
 * commande ET son remboursement reviendrait au même net, mais gonflerait le
 * nombre de commandes et le panier moyen de ventes qui n'ont pas eu lieu.
 */

import type { PeriodRange } from './stats';

// -----------------------------------------------------------------------------
// Constantes partagées
// -----------------------------------------------------------------------------

/**
 * Date de rattachement d'un remboursement.
 *
 *  · `refund_date` — le jour du remboursement. C'est la convention des rapports
 *    Shopify : un remboursement de mars sur une commande de janvier fait
 *    baisser le CA de mars. Les chiffres collent à l'admin Shopify.
 *  · `order_date`  — le jour de la commande remboursée. Le CA d'une période ne
 *    bouge plus une fois ses retours encaissés : c'est la lecture « qualité des
 *    ventes de janvier », mais elle réécrit le passé à chaque remboursement.
 */
export const REFUND_BASES = ['refund_date', 'order_date'] as const;
export type RefundBasis = (typeof REFUND_BASES)[number];

export const REFUND_BASIS_LABELS: Record<RefundBasis, string> = {
  refund_date: 'à la date du remboursement',
  order_date: 'à la date de la commande',
};

/**
 * TVA incluse dans les prix d'abonnement Stripe, en %.
 *
 * Les factures Stripe du compte n'ont aucune TVA ventilée : 9,90 € et 99 €
 * sont des prix TTC à la TVA française (confirmé par l'équipe, 25/09/2026).
 * Le hors taxe se calcule donc TTC ÷ 1,20.
 */
export const SUBSCRIPTION_VAT_PERCENT = 20;

/**
 * TVA retirée des ventes ATTRIBUÉES par les régies (Meta, Google Ads, Amazon
 * Ads) pour les ROAS : les régies rapportent des montants de commandes TTC,
 * les dépenses pub sont HT (autoliquidation). Un ROAS mélangeant les deux
 * serait gonflé d'un cinquième. Taux français : l'essentiel des ventes.
 */
export const AD_SALES_VAT_PERCENT = 20;

/** Ventes attribuées TTC → HT. */
export const attributedExclTax = (valueTtc: number): number => valueTtc / (1 + AD_SALES_VAT_PERCENT / 100);

/** ROAS HT : ventes attribuées HT ÷ dépense HT, deux décimales ; `null` sans dépense. */
export const adsRoas = (attributedTtc: number, costHt: number): number | null =>
  costHt > 0 ? Math.round((attributedExclTax(attributedTtc) / costHt) * 100) / 100 : null;

/**
 * Nombre de pays détaillés dans le camembert et le tableau (les autres se
 * regroupent dans « Autres »). Dix, à la demande de l'équipe : au-delà de six
 * parts un anneau lit mal, d'où la légende chiffrée et le tableau toujours
 * affichés à côté.
 */
export const TOP_COUNTRIES = 10;

/** Nombre de produits du classement. */
export const SALES_TOP_PRODUCTS_LIMIT = 15;

// -----------------------------------------------------------------------------
// GET /api/sales
// -----------------------------------------------------------------------------

export interface SalesQuery {
  /** Premier jour inclus, `YYYY-MM-DD`, en heure de Paris. */
  from: string;
  /** Dernier jour inclus, `YYYY-MM-DD`, en heure de Paris. */
  to: string;
  /** Défaut : `refund_date`. */
  refundBasis?: RefundBasis;
}

/**
 * Totaux d'une fenêtre. Montants en devise de la boutique, TTC.
 */
/** kit Piano LED, piano (Donner, « Piano compatible »), abonnement vendu sur le site, le reste (accessoires, port…). */
export type SalesCategory = 'kit' | 'piano' | 'subscription' | 'other';

export const SALES_CATEGORY_LABELS: Record<SalesCategory, string> = {
  kit: 'Piano LED',
  piano: 'Pianos',
  subscription: 'Abonnements (site)',
  other: 'Autres',
};

export interface SalesCategoryTotals {
  /** Part payée (port compris) avant remboursements. */
  grossRevenue: number;
  refunds: number;
  netRevenue: number;
  netRevenueExclTax: number;
  /** Unités gardées (retours déduits). */
  units: number;
  /** Unités gardées × coût unitaire (kit : coût de revient de sa période ; sinon coût Shopify), € HT. */
  cogs: number;
  /** Unités gardées sans coût connu (COGS sous-estimé). */
  cogsMissingUnits: number;
  /** Unités gardées dans une commande payante — kit à 0 € d'un pack compris ; hors commandes gratuites (SAV, offerts). */
  paidUnits: number;
}

export interface SalesTotals {
  /** Commandes passées dans la fenêtre. */
  orders: number;
  /** Somme payée par les clients, port et taxes compris, avant remboursements. */
  grossRevenue: number;
  /** Remboursements rattachés à la fenêtre, selon `refundBasis`. */
  refunds: number;
  /** `grossRevenue − refunds`. */
  netRevenue: number;
  /**
   * CA net HORS TAXES : le net, multiplié par la part hors taxe des ventes
   * brutes de la fenêtre (TVA réelle des commandes Shopify, port compris).
   */
  netRevenueExclTax: number;
  /** TVA collectée sur les commandes de la fenêtre. */
  taxes: number;
  /**
   * Panier moyen : `grossRevenue / orders`, AVANT remboursements.
   *
   * C'est ce que le client a mis dans son panier au moment de payer. Le
   * calculer sur le net mêlerait deux phénomènes (taille des paniers, taux de
   * retour) dans un seul chiffre. `null` sans commande.
   */
  averageOrderValue: number | null;
  /** Unités commandées, toutes lignes confondues (quantité d'origine). */
  units: number;
  /** Kits Piano LED vendus, retours déduits (même règle que la pénétration). */
  kits: number;
  /** Remises accordées, codes promo et remises automatiques confondus. */
  discounts: number;
  /**
   * Coût des produits vendus : unités gardées (retours déduits) × « coût par
   * article » ACTUEL de chaque variante dans Shopify. HT.
   */
  cogs: number;
  /** Unités vendues dont la variante n'a pas de coût dans Shopify (COGS sous-estimé). */
  cogsMissingUnits: number;
  /** Sessions de la boutique (ShopifyQL). `null` : pas encore lues pour cette fenêtre. */
  sessions: number | null;
  /** CR% : commandes ÷ sessions × 100. `null` sans sessions. */
  conversionRatePercent: number | null;
  /**
   * CA par catégorie de produit. Montants de commande (port, TVA,
   * remboursements) répartis au prorata du CA de lignes de chaque catégorie
   * dans la commande, comme « Performances by product » : la somme des quatre
   * redonne `netRevenue` / `netRevenueExclTax`.
   */
  categories: Record<SalesCategory, SalesCategoryTotals>;
  /**
   * Kits amenés par un clic publicitaire, par régie, d'après les UTM de la
   * dernière visite avant la commande (`shopify_orders.ad_click`). Ce que la
   * boutique a VU arriver par une pub, pas ce que la régie revendique : les
   * clics sans UTM ne comptent pas, c'est une borne basse. Nourrit le
   * « ROAS réel » du Résumé.
   */
  adClicks: Record<AdClickPlatform, AdClickKits>;
}

export type AdClickPlatform = 'meta' | 'google';

export interface AdClickKits {
  /** Kits gardés (retours déduits). */
  kits: number;
  /** CA HT de ces kits (lignes nettes de remises, part de TVA déduite). */
  revenueExclTax: number;
}

/** Une fenêtre de dates et ses totaux. */
export interface SalesWindow {
  from: string;
  to: string;
  totals: SalesTotals;
}

/**
 * La période, et ses deux références.
 *
 *  · `previous` — la période immédiatement antérieure, de MÊME longueur.
 *  · `yearAgo`  — les mêmes dates un an avant (29 février → 28 février).
 *
 * Mêmes définitions que la pénétration de la vue d'ensemble : les deux
 * ensemble, parce que l'une est sensible à la saisonnalité et l'autre mêle
 * douze mois de changements.
 */
export interface SalesComparison {
  current: SalesWindow;
  previous: SalesWindow;
  yearAgo: SalesWindow;
  /**
   * `previous` et `yearAgo` se recouvrent-elles ? Dès un an de période, elles
   * désignent la même tranche : le front n'affiche alors qu'une comparaison.
   */
  comparisonsOverlap: boolean;
}

/** Un jour. Séries SANS trous : un jour sans vente vaut 0. */
export interface SalesDailyPoint {
  /** `YYYY-MM-DD`, en heure de Paris. */
  date: string;
  orders: number;
  grossRevenue: number;
  refunds: number;
  netRevenue: number;
  /** CA net hors taxes du jour (TVA réelle des commandes du jour). */
  netRevenueExclTax: number;
  /** Unités commandées. */
  units: number;
  /** COGS du jour (cf. `SalesTotals.cogs`). */
  cogs: number;
  /** Sessions de la boutique ce jour-là (ShopifyQL). `null` : pas encore lues. */
  sessions: number | null;
}

/**
 * Séries quotidiennes des trois fenêtres.
 *
 * Même longueur pour `current` et `previous` ; `yearAgo` peut compter un jour
 * de moins ou de plus quand la période enjambe un 29 février. Le front aligne
 * par RANG (1er jour contre 1er jour), pas par date.
 */
export interface SalesDailySeries {
  current: SalesDailyPoint[];
  previous: SalesDailyPoint[];
  yearAgo: SalesDailyPoint[];
}

export interface ProductSalesFigures {
  units: number;
  /** Prix × quantité, moins les remises allouées à la ligne (remises de commande comprises). TTC. */
  revenue: number;
}

/**
 * Un produit du classement.
 *
 * Classé sur le CA de la période ; `previous` et `yearAgo` portent les chiffres
 * du MÊME produit dans les fenêtres de référence (0 s'il ne s'y vendait pas).
 */
export interface TopProductRow {
  /** Identifiant produit Shopify, ou le titre si le produit a été supprimé. */
  productKey: string;
  /** Titre le plus récent. */
  title: string;
  current: ProductSalesFigures;
  previous: ProductSalesFigures;
  yearAgo: ProductSalesFigures;
}

export interface SalesTodayFigures {
  orders: number;
  /** Brut : les remboursements du jour n'ont pas de sens sur une journée entamée. */
  grossRevenue: number;
}

/**
 * La journée en cours, jusqu'au dernier sync.
 *
 * Seul bloc qui échappe à la règle « toute période s'arrête à hier » : c'est
 * l'intérêt d'un sync toutes les dix minutes. Les deux références sont coupées
 * à la MÊME heure — comparer une journée entamée à une journée pleine
 * annoncerait une chute chaque matin.
 *
 * Références à la semaine près (J−7 et J−364), pas au jour calendaire : sur UNE
 * journée, l'écart entre un mardi et un samedi écrase tout le reste.
 */
export interface SalesToday {
  /** Jour concerné, `YYYY-MM-DD` à Paris. */
  date: string;
  /** Heure de coupure (ISO 8601) : le début du dernier sync réussi. */
  asOf: string;
  current: SalesTodayFigures;
  /** Même jour de la semaine précédente, même heure. */
  lastWeek: SalesTodayFigures;
  /** Même jour de semaine 52 semaines plus tôt, même heure. */
  lastYear: SalesTodayFigures;
}

export interface SalesMeta {
  generatedAt: string;
  cached: boolean;
  /**
   * Instant jusqu'auquel les données sont complètes (ISO 8601) : le DÉBUT du
   * dernier sync réussi. `null` tant qu'aucun sync n'a abouti.
   */
  lastSyncAt: string | null;
  /** Le passage le plus récent a-t-il échoué ? Les chiffres sont alors ceux du précédent. */
  lastSyncFailed: boolean;
  /** Un import complet de l'historique a-t-il abouti au moins une fois ? */
  historyImported: boolean;
  /** Commande la plus récente en base (ISO 8601). */
  lastOrderAt: string | null;
}

/** CA boutique net d'un pays de livraison, dans les trois fenêtres. */
export interface CountryRevenueRow {
  /** Code ISO 3166-1 (`FR`), `null` pour une commande sans adresse. */
  country: string | null;
  current: number;
  previous: number;
  yearAgo: number;
}

export interface CountryPerformanceRow {
  /** Code ISO 3166-1 (`FR`), `null` pour une commande sans adresse. */
  country: string | null;
  orders: number;
  grossRevenue: number;
  refunds: number;
  netRevenue: number;
  netRevenueExclTax: number;
  units: number;
  cogs: number;
}

export interface ProductPerformanceRow extends Omit<CountryPerformanceRow, 'country'> {
  /** `product_id`, ou `t:<titre>` pour un produit supprimé du catalogue. */
  productKey: string;
  /** Titre le plus récent du produit. */
  title: string;
  /**
   * Commandes contenant le produit (base de l'AOV). `orders`, lui, compte les
   * commandes AU PRORATA (base des Shipping costs).
   */
  ordersWithProduct: number;
}

export interface SalesStats {
  range: PeriodRange;
  refundBasis: RefundBasis;
  /** Devise de la boutique (ISO 4217). */
  currency: string;
  comparison: SalesComparison;
  daily: SalesDailySeries;
  topProducts: TopProductRow[];
  /** Tous les pays de la période, par CA décroissant. Leur somme = CA boutique net. */
  countries: CountryRevenueRow[];
  /**
   * Période COURANTE par pays de livraison, mêmes règles que les tuiles.
   * Tous les pays, non triés — le front garde les dix premiers.
   */
  countryPerformance: CountryPerformanceRow[];
  /**
   * Période COURANTE, CA HT net par jour et par pays — seulement les couples
   * (jour, pays) avec une commande ou un remboursement.
   */
  countryDaily: Array<{ date: string; country: string | null; netRevenueExclTax: number }>;
  /**
   * Période COURANTE par site d'origine de la commande (`google`, `direct`,
   * `facebook`…, dernière visite du parcours client). `site` null : inconnu.
   */
  sitePerformance: Array<Omit<CountryPerformanceRow, 'country'> & { site: string | null }>;
  /** Période COURANTE, CA HT net par jour et par site (couples avec activité seulement). */
  siteDaily: Array<{ date: string; site: string | null; netRevenueExclTax: number }>;
  /**
   * Période COURANTE par produit. Les montants de commande (payé, TVA, port,
   * remboursements) sont répartis entre ses produits au prorata du CA de leurs
   * lignes : la somme des produits redonne les tuiles.
   */
  productPerformance: ProductPerformanceRow[];
  /** Période COURANTE, CA HT net par jour et par produit (couples avec activité seulement). */
  productDaily: Array<{ date: string; productKey: string; netRevenueExclTax: number }>;
  /** `null` tant qu'aucun sync n'a abouti. */
  today: SalesToday | null;
  meta: SalesMeta;
}

export interface SalesStatsResponse {
  stats: SalesStats;
}

// -----------------------------------------------------------------------------
// GET /api/sales/subscriptions — abonnements Stripe
// -----------------------------------------------------------------------------

/**
 * Totaux abonnements d'une fenêtre.
 *
 * ── Un abonné, c'est un abonnement PAYANT ───────────────────────────────────
 * Un abonnement entre dans les compteurs le jour où il devient payant : sa
 * date de début, ou la fin de son essai gratuit. Un essai abandonné n'est ni
 * un abonné, ni une résiliation.
 *
 * ── « Début » et « fin » de fenêtre ─────────────────────────────────────────
 * Début = minuit du premier jour, fin = minuit après le dernier jour (heure de
 * Paris). `activeAtEnd` est donc le parc au soir du dernier jour affiché.
 */
/**
 * Variante du test A/B du site dans laquelle l'abonnement est PROPOSÉ avec le
 * kit (attribut de commande `_eabTestsList`, cf. `shopify_orders.ab_tests`).
 * Le taux d'attache ne compte que ces commandes au dénominateur : les clients
 * de l'autre variante ne pouvaient pas le prendre.
 */
export const SITE_SUBSCRIPTION_AB_VARIANT = '57100';

/**
 * Jour (heure de Paris) à partir duquel l'abonnement est proposé sur les DEUX
 * variantes du test A/B du site : site A à 70 €, site B à 90 €. Avant, seule la
 * variante `SITE_SUBSCRIPTION_AB_VARIANT` le proposait ; à partir de ce jour
 * toutes les commandes de kit entrent au dénominateur de l'attache, et le bloc
 * « Abonnement vendu sur le site » se découpe par variante.
 */
export const SITE_SUBSCRIPTION_BOTH_VARIANTS_FROM = '2026-11-04';

/**
 * Tests successifs de l'abonnement vendu sur le site. Chacun a sa section dans
 * la page, calculée sur la période affichée coupée à ses dates, et masquée si
 * la période ne le recoupe pas. Bornes en jours de Paris, incluses ; `from`
 * `null` : depuis la première vente, `to` `null` : en cours.
 */
export const SITE_SUBSCRIPTION_TESTS = [
  {
    key: 'variant-57100',
    label: 'Test 1 — abonnement proposé sur une seule variante',
    detail: `variante ${SITE_SUBSCRIPTION_AB_VARIANT} du test A/B, l'autre ne le proposait pas`,
    from: null,
    to: '2026-11-03',
    allVariants: false,
  },
  {
    key: 'sites-a-b',
    label: 'Test 2 — site A à 70 € / site B à 90 €',
    detail: 'abonnement proposé sur les deux variantes du test A/B, à deux prix',
    from: SITE_SUBSCRIPTION_BOTH_VARIANTS_FROM,
    to: null,
    allVariants: true,
  },
] as const satisfies ReadonlyArray<{
  key: string;
  label: string;
  detail: string;
  from: string | null;
  to: string | null;
  allVariants: boolean;
}>;

/** Une variante du test de prix, sur un test à toutes variantes. */
export interface SiteSubscriptionVariant {
  /** « A », « B »… dans l'ordre des prix croissants (A = le moins cher). */
  site: string;
  /** Identifiant de la variante dans `_eabTestsList`. */
  variant: string;
  /** Prix payé le plus fréquent (TTC, offerts exclus), `null` sans vente. */
  price: number | null;
  kitOrders: number;
  kitOrdersWithSubscription: number;
  attachRatePercent: number | null;
  /** Abonnements vendus (unités, offerts exclus). */
  units: number;
}

export interface SubscriptionTotals {
  /** Factures d'abonnement encaissées dans la fenêtre, TTC. */
  revenue: number;
  /** Remboursements Stripe aboutis, à leur date. */
  refunds: number;
  /**
   * Abonnements vendus sur le site (Shopify, « Abonnement 1 an »), TTC remises
   * déduites, le jour de la commande. Déjà comptés dans le CA Shopify : le
   * Résumé les en retire pour ne pas les compter deux fois.
   */
  siteRevenue: number;
  /** Nombre d'abonnements vendus sur le site. */
  siteUnits: number;
  /** `revenue − refunds + siteRevenue`. */
  netRevenue: number;
  /**
   * CA net hors taxes : `netRevenue ÷ (1 + TVA)`. Les prix Stripe sont TTC à
   * TVA française, sans TVA ventilée sur les factures — cf.
   * `SUBSCRIPTION_VAT_PERCENT`.
   */
  netRevenueExclTax: number;
  /**
   * CA encaissé dans la fenêtre sur les abonnements devenus payants DANS la
   * fenêtre : ce que les nouveaux abonnés ont déjà rapporté. Sert au ROAS
   * payback.
   */
  newSubscribersRevenue: number;
  /**
   * MRR des abonnements devenus payants dans la fenêtre : chaque nouvel abonné
   * au prix d'un mois (8,25 € pour un annuel, 9,90 € pour un mensuel, TTC).
   * Numérateur du ROAS payback.
   */
  newSubscribersMrr: number;
  /** Factures payées (hors factures à 0 comprises). */
  invoices: number;

  /** Abonnés payants au début de la fenêtre. */
  activeAtStart: number;
  /**
   * Abonnés payants à la fin de la fenêtre, définition de Stripe : abonnements
   * à MRR positif, essais exclus, et sortis des compteurs dès la DEMANDE de
   * résiliation (pas à la fin de la période payée). Facturation en pause
   * exclue.
   */
  activeAtEnd: number;
  /** Dont annuels / mensuels, à la fin. */
  annualAtEnd: number;
  monthlyAtEnd: number;
  /**
   * Revenu mensuel récurrent à la fin de la fenêtre, défini comme le
   * Dashboard Stripe : prix mensualisé de chaque abonné actif (annuel ÷ 12),
   * moins ses réductions permanentes (réglage du compte). Hors taxes (aucune taxe
   * ventilée sur les prix du compte).
   *
   * Écart assumé avec Stripe : un abonnement vendu sur le site (payé chez
   * Shopify) compte dès que son code est ACTIVÉ dans Stripe, au prix Stripe de
   * son abonnement — Stripe, lui, ne le compte qu'à la fin de son année offerte.
   * `mrrAtEnd = mrrStripeAtEnd + mrrSiteAtEnd`.
   */
  mrrAtEnd: number;
  /** Part des abonnés Stripe « classiques » (devenus payants). */
  mrrStripeAtEnd: number;
  /** Part des abonnements vendus sur le site et activés dans Stripe, et leur nombre. */
  mrrSiteAtEnd: number;
  mrrSiteSubscribersAtEnd: number;

  /** Abonnements devenus payants dans la fenêtre. */
  newSubscribers: number;
  /** Résiliations DEMANDÉES dans la fenêtre (comme Stripe), quelle que soit la date de fin effective. */
  churned: number;
  /**
   * Taux de churn, formule de Stripe : départs de la fenêtre ÷ (abonnés au
   * début + nouveaux abonnés de la fenêtre), en %. `null` sans aucune base.
   */
  churnRatePercent: number | null;

  /**
   * Kits Piano LED vendus sur Shopify ET Amazon depuis la création du PREMIER
   * abonnement Stripe, jusqu'à la fin de la fenêtre. Les kits vendus avant n'entrent pas :
   * leurs acheteurs n'avaient pas d'abonnement à souscrire.
   */
  kitsSoldToDate: number;
  /**
   * Pénétration : abonnés payants à la fin ÷ kits vendus depuis le premier
   * abonnement, en %. `null` sans kit (fenêtre antérieure aux abonnements).
   */
  penetrationPercent: number | null;

  /** Essais gratuits démarrés dans la fenêtre (hors abonnements vendus sur le site, déjà payés). */
  trialsStarted: number;
  /** Essais en cours à la fin de la fenêtre (pas encore payants, pas abandonnés). */
  trialsActiveAtEnd: number;
  /** Essais terminés dans la fenêtre — arrivés à échéance ou abandonnés avant. */
  trialsEnded: number;
  /** Parmi eux, ceux devenus payants. */
  trialsConverted: number;
  /**
   * Ventilation app / site. Un abonnement vendu sur le site (« Abonnement 1 an
   * Virtuoso », payé chez Shopify) commence chez Stripe par un « essai » d'un
   * an qui convertit d'office : il n'entre pas dans `trialsStarted` ni dans le
   * taux de conversion, mais il est compté ici pour l'afficher à part.
   *
   * Côté souscriptions, un abonnement du site n'est validé qu'à la FIN de son
   * essai (il devient payant chez Stripe, même si sa première année est offerte
   * par le coupon) : `newSubscribers = newSubscribersApp + newSubscribersSite`.
   */
  trialsStartedSite: number;
  trialsActiveAtEndSite: number;
  newSubscribersApp: number;
  newSubscribersSite: number;
  /**
   * Conversion des essais : `trialsConverted / trialsEnded`, en %. Même
   * définition que Stripe (essais convertis ÷ essais terminés sur la période).
   * `null` si aucun essai ne s'est terminé.
   */
  trialConversionPercent: number | null;

  /**
   * Renouvellements d'année 2 : abonnements ANNUELS dont la première échéance
   * annuelle (un an après leur passage en payant) tombe dans la fenêtre, et
   * leur issue évaluée aujourd'hui.
   */
  renewalsDue: number;
  /** Facture payée autour de l'échéance (7 jours avant, 35 après). */
  renewalsRenewed: number;
  /** Résiliation demandée avant l'échéance. */
  renewalsCanceled: number;
  /** Échéance passée depuis plus de 35 jours ou abonnement terminé, sans paiement. */
  renewalsFailed: number;
  /** Échéance passée depuis moins de 35 jours, paiement pas encore abouti (relances Stripe). */
  renewalsPending: number;
  /** `renewed ÷ (renewed + canceled + failed)`, en %, les échéances en attente exclues. `null` sans issue connue. */
  renewalRatePercent: number | null;
  /** Encaissé sur les renouvellements, TTC. */
  renewalsRevenue: number;
  /** Dont abonnés n'ayant rien payé la première année (coupon, code du site), et parmi eux les renouvelés. */
  renewalsDueFreeFirstYear: number;
  renewalsRenewedFreeFirstYear: number;
}

export interface SubscriptionWindow {
  from: string;
  to: string;
  totals: SubscriptionTotals;
}

export interface SubscriptionComparison {
  current: SubscriptionWindow;
  previous: SubscriptionWindow;
  yearAgo: SubscriptionWindow;
  comparisonsOverlap: boolean;
}

/** Un jour. Série sans trous. */
export interface SubscriptionDailyPoint {
  date: string;
  /** CA abonnements du jour, remboursements déduits, ventes du site comprises. */
  netRevenue: number;
  /** Dont abonnements vendus sur le site (déjà dans le CA Shopify du jour). */
  siteRevenue: number;
  /** Abonnés payants le soir de ce jour. */
  activeSubscribers: number;
  /** MRR TTC le soir de ce jour (même définition que `mrrAtEnd`). */
  mrr: number;
  /** Abonnements devenus payants ce jour-là (cf. `newSubscribers`). */
  newSubscribers: number;
  /** Résiliations demandées ce jour-là (cf. `churned`). */
  churned: number;
  /** Essais gratuits en cours le soir de ce jour, hors abonnements du site (cf. `trialsActiveAtEnd`). */
  trialsActive: number;
  /** Essais gratuits démarrés ce jour-là, hors abonnements du site (cf. `trialsStarted`). */
  trialsStarted: number;
}

export interface SubscriptionSalesMeta {
  generatedAt: string;
  cached: boolean;
  /** Début du dernier sync Stripe réussi. */
  lastSyncAt: string | null;
  lastSyncFailed: boolean;
  historyImported: boolean;
}

/**
 * Parc et MRR à l'instant du dernier sync, sur le statut courant des
 * abonnements (`active` ou `past_due`, MRR positif). C'est le chiffre à
 * rapprocher du Dashboard Stripe ; les totaux de fenêtre, eux, sont arrêtés
 * au soir du dernier jour de la période.
 */
export interface SubscriptionLive {
  activeSubscribers: number;
  /** `mrrStripe + mrrSite` ; les abonnements du site comptent dès leur activation dans Stripe. */
  mrr: number;
  mrrStripe: number;
  mrrSite: number;
  siteSubscribers: number;
  /** Début du dernier sync réussi. */
  asOf: string | null;
}

/**
 * Abonnement vendu sur le site, fenêtre courante : le test de prix et ce qu'il
 * donne côté Stripe.
 */
export interface SiteSubscriptionSales {
  /** Ventes par prix unitaire payé TTC (0 : offert), la plus vendue d'abord. */
  prices: Array<{ unitPrice: number; units: number; revenue: number }>;
  /** Prix moyen payé, offerts compris. `null` sans vente. */
  averagePrice: number | null;
  /** Commandes Shopify contenant un kit, et parmi elles celles avec l'abonnement. */
  kitOrders: number;
  kitOrdersWithSubscription: number;
  /** `kitOrdersWithSubscription / kitOrders`, en %. `null` sans commande de kit. */
  attachRatePercent: number | null;
  /**
   * Découpage par variante (site A / site B) d'un test à toutes variantes.
   * Vide sinon, ou tant que les deux variantes n'ont pas chacune une vente.
   */
  variants: SiteSubscriptionVariant[];
  /** Codes du site utilisés dans Stripe dans la période (à la souscription, essai compris). */
  activations: number;
  /** Abonnés actifs venus du site, à la fin de la période. */
  activeAtEnd: number;
  /** Revenu annuel TTC d'un abonné Stripe hors site (médiane) : annuel, et mensuel × 12. */
  stripeAnnualPrice: number | null;
  stripeMonthlyPricePerYear: number | null;
}

/** Un test de `SITE_SUBSCRIPTION_TESTS`, sur la période affichée coupée à ses dates. */
export interface SiteSubscriptionTestSales extends SiteSubscriptionSales {
  key: string;
  label: string;
  detail: string;
  /** Dates du test (première vente pour le premier) ; `to` `null` : en cours. */
  testFrom: string | null;
  testTo: string | null;
  /** Partie de la période affichée couverte par le test (jours inclus). */
  from: string;
  to: string;
  /** Abonnements vendus (offerts exclus) et CA TTC sur cette partie. */
  units: number;
  revenue: number;
}

/** Bloc « Abonnement vendu sur le site ». */
export interface SiteSubscriptionBlock {
  /** Depuis la première vente, quelle que soit la période. */
  allTime: { units: number; revenue: number; since: string | null };
  /** Tests qui recoupent la période, dans l'ordre chronologique. */
  tests: SiteSubscriptionTestSales[];
}

/** Échéances d'année 2 d'un mois (heure de Paris), issue évaluée aujourd'hui. */
export interface RenewalMonth {
  /** `YYYY-MM`. */
  month: string;
  due: number;
  renewed: number;
  canceled: number;
  failed: number;
  pending: number;
  /** Échéance pas encore arrivée (hors résiliations déjà demandées, comptées dans `canceled`). */
  upcoming: number;
  revenue: number;
  /** `renewed ÷ due`, en %. `null` tant qu'aucune échéance du mois n'est passée. */
  renewalRatePercent: number | null;
}

/**
 * Valeur vie (LTV), estimée — l'historique Stripe commence en août 2025,
 * l'estimation est assumée (cf. README, « LTV »).
 *
 * `value` est la LTV **par acheteur de kit** : la valeur d'un abonné
 * (`subscriberValue`) × le taux d'abonnement des acheteurs (`attachRatePercent`),
 * ceux qui ne s'abonnent jamais comptant pour zéro. C'est elle qui entre dans
 * le ROAS blended.
 *
 *  · annuel  : prix annuel × années attendues, avec années = 1 ÷ (1 − taux de
 *    renouvellement à l'année 2), plafonné à `ANNUAL_YEARS_CAP` ;
 *  · mensuel : prix mensuel × mois attendus, avec mois = 1 ÷ churn mensuel
 *    (moyenne des 12 derniers mois entiers, formule Stripe), plafonné ;
 *  · `value` : pondération des deux selon la part d'annuels parmi les abonnés
 *    des 12 derniers mois. TTC ; `valueExclTax` = ÷ 1,20.
 * `null` quand une brique manque (aucune échéance d'année 2 passée, aucun
 * mensuel…).
 */
export interface SubscriptionLtv {
  /** Par acheteur de kit, TTC / HT. */
  value: number | null;
  valueExclTax: number | null;
  /** Par abonné, TTC / HT. */
  subscriberValue: number | null;
  subscriberValueExclTax: number | null;
  /** Abonnements devenus payants ÷ kits vendus (Shopify + Amazon) depuis le premier abonnement, en %. */
  attachRatePercent: number | null;
  kitsSold: number;
  subscribersEver: number;
  annual: {
    price: number | null;
    /** Renouvelés ÷ échéances d'année 2 à l'issue connue, en %. */
    renewalRatePercent: number | null;
    /** Échéances d'année 2 à l'issue connue (la base du taux). */
    settled: number;
    expectedYears: number | null;
    value: number | null;
  };
  monthly: {
    price: number | null;
    /** Churn mensuel moyen des 12 derniers mois entiers, en %. */
    churnRatePercent: number | null;
    expectedMonths: number | null;
    value: number | null;
  };
  /** Part d'annuels parmi les abonnements devenus payants sur les 12 derniers mois, en %. */
  annualSharePercent: number | null;
}

export interface SubscriptionSalesStats {
  range: PeriodRange;
  currency: string;
  comparison: SubscriptionComparison;
  live: SubscriptionLive;
  siteSales: SiteSubscriptionBlock;
  ltv: SubscriptionLtv;
  /** Échéances d'année 2 par mois, de la première jusqu'à trois mois devant. */
  renewalMonths: RenewalMonth[];
  /**
   * Abonnements de la période : devenus payants dans la période et encore
   * actifs à sa fin, par pays de facturation (dernière facture payée) et par
   * rythme (`year`, `month`). `country` null : pays inconnu.
   */
  periodSubscriptions: Array<{ country: string | null; billingInterval: string | null; subscribers: number }>;
  daily: {
    current: SubscriptionDailyPoint[];
    previous: SubscriptionDailyPoint[];
    yearAgo: SubscriptionDailyPoint[];
  };
  meta: SubscriptionSalesMeta;
}

export interface SubscriptionSalesStatsResponse {
  stats: SubscriptionSalesStats;
}

// -----------------------------------------------------------------------------
// GET /api/sales/ads — publicité Meta
// -----------------------------------------------------------------------------

/**
 * Totaux publicitaires d'une fenêtre.
 *
 * Les achats et leur valeur sont ceux qu'ATTRIBUE Meta (fenêtre d'attribution
 * du compte) : sa vision, qui revendique aussi des ventes que d'autres canaux
 * ont amenées. Le front les confronte au CA boutique réel (ROAS global).
 */
export interface AdsTotals {
  spend: number;
  impressions: number;
  clicks: number;
  metaPurchases: number;
  metaPurchaseValue: number;
}

export interface AdsWindow {
  from: string;
  to: string;
  totals: AdsTotals;
}

export interface AdsComparison {
  current: AdsWindow;
  previous: AdsWindow;
  yearAgo: AdsWindow;
  comparisonsOverlap: boolean;
}

/** Un jour, du fuseau du compte publicitaire. Série sans trous. */
export interface AdsDailyPoint {
  date: string;
  spend: number;
  metaPurchaseValue: number;
}

export interface AdsCampaignFigures {
  spend: number;
  metaPurchases: number;
  metaPurchaseValue: number;
}

/** Une campagne, classée par dépense sur la période. */
export interface AdsCampaignRow {
  campaignId: string;
  name: string;
  current: AdsCampaignFigures;
  previous: AdsCampaignFigures;
  yearAgo: AdsCampaignFigures;
}

export interface AdsMeta {
  generatedAt: string;
  cached: boolean;
  lastSyncAt: string | null;
  lastSyncFailed: boolean;
  historyImported: boolean;
  accountName: string | null;
  /**
   * Fuseau du compte publicitaire. S'il n'est pas `Europe/Paris`, ses journées
   * ne coïncident pas exactement avec celles de Shopify et le front le dit.
   */
  accountTimezone: string | null;
  /**
   * Devise de facturation du compte publicitaire (USD pour Piano LED). Tous
   * les montants de la réponse sont CONVERTIS en euros, au taux de référence
   * BCE du jour de chaque dépense.
   */
  accountCurrency: string | null;
  /**
   * Lignes campagne × jour restées sans taux de change dans les trois
   * fenêtres. Non nul : les totaux sont sous-estimés, le front le signale.
   */
  missingExchangeRates: number;
}

export interface AdsStats {
  range: PeriodRange;
  /** Toujours `EUR` : les montants sont convertis. */
  currency: string;
  comparison: AdsComparison;
  daily: { current: AdsDailyPoint[]; previous: AdsDailyPoint[]; yearAgo: AdsDailyPoint[] };
  campaigns: AdsCampaignRow[];
  meta: AdsMeta;
}

export interface AdsStatsResponse {
  stats: AdsStats;
}

/** Nombre de campagnes du classement. */
export const ADS_TOP_CAMPAIGNS_LIMIT = 15;

// -----------------------------------------------------------------------------
// GET /api/sales/amazon — ventes Amazon (Selling Partner API)
// -----------------------------------------------------------------------------

/**
 * Totaux Amazon d'une fenêtre, CONVERTIS en euros (taux BCE du jour de chaque
 * commande). Commandes annulées exclues.
 *
 * ⚠️ Pas de remboursements : le rapport de commandes ne les porte pas (ils
 * vivent dans les événements financiers d'Amazon). Le CA Amazon est donc un CA
 * vendu, pas un CA net de retours.
 */
export interface AmazonTotals {
  orders: number;
  units: number;
  /** Articles + port − promotions, TTC. */
  revenue: number;
  /** Le même, hors taxes (TVA indiquée par Amazon sur chaque ligne). */
  revenueExclTax: number;
  taxes: number;
  /** Kits Piano LED (même règle de titre que Shopify). */
  kits: number;
  /** CA des kits, TTC et HT (euros), hors annulations — pour le ROAS. */
  kitsRevenue: number;
  kitsRevenueExclTax: number;
  /** Kits × coût de revient en vigueur à la date d'achat (€ HT) ; `kitsWithoutCost` : kits sans période de coût. */
  kitsCogs: number;
  kitsWithoutCost: number;
  /** Kits vendus à un prix > 0 € (hors remplacements gratuits). */
  paidKits: number;
  /** Dont Amazon Business (commandes professionnelles). */
  businessRevenue: number;
}

export interface AmazonWindow {
  from: string;
  to: string;
  totals: AmazonTotals;
}

export interface AmazonDailyPoint {
  date: string;
  orders: number;
  revenue: number;
}

export interface AmazonProductRow {
  asin: string;
  title: string;
  current: ProductSalesFigures;
  previous: ProductSalesFigures;
  yearAgo: ProductSalesFigures;
}

export interface AmazonMarketplaceShare {
  /** « Amazon.fr », « Amazon.de »… */
  salesChannel: string;
  revenue: number;
  orders: number;
}

export interface AmazonMeta {
  generatedAt: string;
  cached: boolean;
  lastSyncAt: string | null;
  lastSyncFailed: boolean;
  historyImported: boolean;
  /** Lignes restées sans taux de change : totaux sous-estimés si non nul. */
  missingExchangeRates: number;
}

export interface AmazonStats {
  range: PeriodRange;
  currency: string;
  comparison: {
    current: AmazonWindow;
    previous: AmazonWindow;
    yearAgo: AmazonWindow;
    comparisonsOverlap: boolean;
  };
  daily: { current: AmazonDailyPoint[]; previous: AmazonDailyPoint[]; yearAgo: AmazonDailyPoint[] };
  topProducts: AmazonProductRow[];
  /** Par pays de livraison, période courante et références. */
  countries: CountryRevenueRow[];
  marketplaces: AmazonMarketplaceShare[];
  meta: AmazonMeta;
}

export interface AmazonStatsResponse {
  stats: AmazonStats;
}

// -----------------------------------------------------------------------------
// GET /api/sales/amazon-ads — publicité Amazon (Ads API)
// -----------------------------------------------------------------------------

/** Totaux publicitaires Amazon, convertis en euros. */
export interface AmazonAdsTotals {
  spend: number;
  impressions: number;
  clicks: number;
  /** Commandes et ventes ATTRIBUÉES par Amazon (7 j Sponsored Products, 14 j Brands/Display). */
  attributedOrders: number;
  attributedSales: number;
}

export interface AmazonAdsWindow {
  from: string;
  to: string;
  totals: AmazonAdsTotals;
}

export interface AmazonAdsDailyPoint {
  date: string;
  spend: number;
  attributedSales: number;
}

export interface AmazonAdsCampaignRow {
  campaignId: string;
  name: string;
  /** SPONSORED_PRODUCTS, SPONSORED_BRANDS, SPONSORED_DISPLAY */
  adProduct: string;
  current: { spend: number; attributedOrders: number; attributedSales: number };
  previous: { spend: number; attributedOrders: number; attributedSales: number };
  yearAgo: { spend: number; attributedOrders: number; attributedSales: number };
}

export interface AmazonAdsMeta {
  generatedAt: string;
  cached: boolean;
  lastSyncAt: string | null;
  lastSyncFailed: boolean;
  historyImported: boolean;
  missingExchangeRates: number;
  /**
   * Premier jour couvert par les données publicitaires. Amazon ne conserve que
   * ~95 jours d'historique : une comparaison « l'an dernier » n'a de sens
   * qu'une fois un an de données accumulé par le worker.
   */
  firstDay: string | null;
}

export interface AmazonAdsStats {
  range: PeriodRange;
  currency: string;
  comparison: {
    current: AmazonAdsWindow;
    previous: AmazonAdsWindow;
    yearAgo: AmazonAdsWindow;
    comparisonsOverlap: boolean;
  };
  daily: { current: AmazonAdsDailyPoint[]; previous: AmazonAdsDailyPoint[]; yearAgo: AmazonAdsDailyPoint[] };
  campaigns: AmazonAdsCampaignRow[];
  meta: AmazonAdsMeta;
}

export interface AmazonAdsStatsResponse {
  stats: AmazonAdsStats;
}

// -----------------------------------------------------------------------------
// GET /api/sales/ads/daily — Ads KPIs jour par jour (tableau « Performances »)
// -----------------------------------------------------------------------------

/**
 * Nombre de jours par page du tableau quotidien, et maximum accepté (une
 * année : le graphique « Performances by platform » suit la période de la page).
 */
export const ADS_DAILY_DEFAULT_DAYS = 7;
export const ADS_DAILY_MAX_DAYS = 366;

/** Chiffres bruts d'une plateforme sur une journée, en euros. */
export interface AdsDayFigures {
  cost: number;
  impressions: number;
  clicks: number;
  /** Achats attribués par la plateforme. */
  conversions: number;
  conversionValue: number;
}

export interface AdsDailyKpiRow {
  /** `YYYY-MM-DD` — jour du compte publicitaire. */
  date: string;
  meta: AdsDayFigures;
  /** `null` tant qu'Amazon Ads n'est pas branché. */
  amazon: AdsDayFigures | null;
  /** `null` tant que Google Ads n'est pas branché. */
  google: AdsDayFigures | null;
  /** Piano LED payants (> 0 €) vendus ce jour-là, Shopify + Amazon : dénominateur du CPA global. */
  paidKits: number;
}

export interface AdsDailyKpisResponse {
  from: string;
  to: string;
  /** Dernier jour disponible (hier) : borne de la flèche « jours suivants ». */
  latestDay: string;
  /** Du plus récent au plus ancien, sans trous. */
  days: AdsDailyKpiRow[];
}
